import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import {
  validateClaim,
  registerAccount,
  setToken,
  formatApiErrorDetail,
  CLAIM_REASON_MESSAGES,
} from "@/lib/mastroAuth";
import { MastroBrand } from "@/components/MastroBrand";
import { REGISTRATION } from "@/constants/testIds";

const inputClass =
  "mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none transition-colors placeholder:text-neutral-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20";

const Shell = ({ children }) => (
  <div
    data-testid={REGISTRATION.page}
    className="min-h-screen w-full bg-neutral-50 text-neutral-950 flex flex-col"
  >
    <div className="mx-auto flex w-full max-w-md flex-1 flex-col px-6 py-10">
      <div>
        <MastroBrand className="h-10" />
      </div>
      <div className="mt-10 flex-1">{children}</div>
      <p className="mt-8 text-xs text-neutral-400">
        Il tuo assistente operativo su WhatsApp.
      </p>
    </div>
  </div>
);

export default function Registrati() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const wa = params.get("wa") || "";

  const [phase, setPhase] = useState("loading"); // loading | invalid | form | submitting
  const [reason, setReason] = useState(null);
  const [context, setContext] = useState(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  // MS-L4C — Terms of Service acceptance (v1.0). Required to submit.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    let active = true;
    if (!token) {
      setPhase("invalid");
      setReason("invalid");
      return;
    }
    validateClaim(token)
      .then((data) => {
        if (!active) return;
        if (data.valid) {
          setContext(data);
          setPhase("form");
        } else {
          setReason(data.reason || "invalid");
          setPhase("invalid");
        }
      })
      .catch(() => {
        if (!active) return;
        setReason("invalid");
        setPhase("invalid");
      });
    return () => {
      active = false;
    };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setFormError("Inserisci un'email valida.");
      return;
    }
    if (password.length < 8) {
      setFormError("La password deve avere almeno 8 caratteri.");
      return;
    }
    if (password !== confirm) {
      setFormError("Le password non coincidono.");
      return;
    }
    if (!termsAccepted) {
      setFormError("Devi accettare i Termini di utilizzo per continuare.");
      return;
    }
    setPhase("submitting");
    try {
      const data = await registerAccount({
        whatsapp_identity: wa || undefined,
        claim_token: token,
        password,
        recovery_email: email,
      });
      setToken(data.access_token);
      navigate("/app", { replace: true });
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 400 && /link/i.test(detail)) {
        setReason(/scadut/i.test(detail) ? "expired" : "used");
        setPhase("invalid");
        return;
      }
      setFormError(detail);
      setPhase("form");
    }
  };

  if (phase === "loading") {
    return (
      <Shell>
        <div
          data-testid={REGISTRATION.loading}
          className="flex items-center gap-3 text-neutral-500"
        >
          <Loader2 className="h-5 w-5 animate-spin" />
          Verifica del link in corso…
        </div>
      </Shell>
    );
  }

  if (phase === "invalid") {
    return (
      <Shell>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid={REGISTRATION.error}
          className="rounded-xl border border-red-200 bg-red-50 p-5"
        >
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-semibold">Link non utilizzabile</span>
          </div>
          <p className="mt-2 text-sm text-neutral-700">
            {CLAIM_REASON_MESSAGES[reason] || CLAIM_REASON_MESSAGES.invalid}
          </p>
        </motion.div>
      </Shell>
    );
  }

  return (
    <Shell>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm"
      >
        <h1 className="text-2xl font-bold tracking-tight">Attiva la dashboard</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Imposta le credenziali per accedere anche dal computer. Il tuo lavoro
          su WhatsApp resta invariato.
        </p>

        {context && (
          <div
            data-testid={REGISTRATION.contextInfo}
            className="mt-5 flex items-center gap-2 rounded-lg bg-neutral-100 px-3 py-2 text-sm text-neutral-600"
          >
            <ShieldCheck className="h-4 w-4 text-teal-600" />
            <span>
              {context.profession ? `${context.profession} · ` : ""}
              WhatsApp {context.whatsapp_number}
            </span>
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-neutral-700" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              data-testid={REGISTRATION.emailInput}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@esempio.it"
              className={inputClass}
              autoComplete="email"
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-700" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              data-testid={REGISTRATION.passwordInput}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Almeno 8 caratteri"
              className={inputClass}
              autoComplete="new-password"
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-700" htmlFor="confirm">
              Conferma password
            </label>
            <input
              id="confirm"
              type="password"
              data-testid={REGISTRATION.passwordConfirmInput}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Ripeti la password"
              className={inputClass}
              autoComplete="new-password"
              required
            />
          </div>

          {formError && (
            <p
              data-testid={REGISTRATION.formError}
              className="text-sm font-medium text-red-600"
            >
              {formError}
            </p>
          )}

          {/* MS-L4C — Terms of Service acceptance (v1.0). Required to submit. */}
          <label className="flex items-start gap-2.5 text-sm text-neutral-700">
            <input
              type="checkbox"
              data-testid="register-terms-checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-teal-600 focus:ring-teal-500/40"
            />
            <span>
              Ho letto e accetto i{" "}
              <Link
                to="/termini"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="register-terms-link"
                className="font-medium text-teal-700 underline-offset-4 hover:underline"
              >
                Termini di utilizzo
              </Link>
              .
            </span>
          </label>

          <p className="text-xs text-neutral-500">
            Consulta la{" "}
            <Link
              to="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="register-privacy-link"
              className="text-teal-700 underline-offset-4 hover:underline"
            >
              Privacy Policy
            </Link>{" "}
            per informazioni sul trattamento dei dati.
          </p>

          <button
            type="submit"
            data-testid={REGISTRATION.submitButton}
            disabled={phase === "submitting" || !termsAccepted}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {phase === "submitting" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Attivazione…
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Attiva la dashboard
              </>
            )}
          </button>
        </form>
      </motion.div>
    </Shell>
  );
}
